/**
 * Persists recent directory (/search) queries in localStorage (per device).
 * Broadcasts changes so sidebar + /search stay in sync.
 */

const STORAGE_KEY = 'adieuu.directory-search.recent';
const MAX_RECENT = 8;
const MIN_QUERY_LENGTH = 2;
export const DIRECTORY_RECENT_SEARCHES_CHANGED = 'adieuu:directory-recent-searches-changed';

/** Wait for typing to settle before committing a recent (Search page). */
export const DIRECTORY_RECENT_PERSIST_DEBOUNCE_MS = 1200;

type RecentListener = (recents: string[]) => void;
const listeners = new Set<RecentListener>();

function normalize(query: string): string {
  return query.trim().replace(/\s+/g, ' ');
}

/**
 * Collapse typing-prefix variants (e.g. peo / peop / people → people).
 * Keeps the longer form when two queries share a prefix relationship.
 */
export function collapseDirectoryRecentPrefixes(recents: string[]): string[] {
  const out: string[] = [];
  for (const raw of recents) {
    const q = normalize(raw);
    if (q.length < MIN_QUERY_LENGTH) continue;
    const lower = q.toLowerCase();
    let merged = false;
    for (let i = 0; i < out.length; i++) {
      const existing = out[i]!;
      const el = existing.toLowerCase();
      if (el === lower) {
        merged = true;
        break;
      }
      if (el.startsWith(lower) || lower.startsWith(el)) {
        if (q.length >= existing.length) {
          out[i] = q;
        }
        merged = true;
        break;
      }
    }
    if (!merged) out.push(q);
  }
  return out.slice(0, MAX_RECENT);
}

function emitChanged(recents: string[]): void {
  for (const listener of listeners) {
    try {
      listener(recents);
    } catch {
      // ignore subscriber errors
    }
  }
  if (typeof window === 'undefined') return;
  // Prefer a plain Event for happy-dom compatibility (CustomEvent can throw).
  try {
    const event = new Event(DIRECTORY_RECENT_SEARCHES_CHANGED);
    Object.defineProperty(event, 'detail', { value: recents, enumerable: true });
    window.dispatchEvent(event);
  } catch {
    // ignore environments that reject Event construction
  }
}

/** Subscribe to in-process recent-search changes (preferred over DOM events in tests). */
export function subscribeDirectoryRecentSearches(listener: RecentListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Recent directory searches, newest first.
 */
export function loadDirectoryRecentSearches(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return collapseDirectoryRecentPrefixes(
      parsed.filter((row): row is string => typeof row === 'string'),
    );
  } catch {
    return [];
  }
}

function persist(next: string[]): string[] {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // ignore quota / private mode
  }
  emitChanged(next);
  return next;
}

/**
 * Add or move a query to the front; dedupes case-insensitively;
 * collapses typing-prefix variants; caps length.
 */
export function addDirectoryRecentSearch(query: string): string[] {
  const normalized = normalize(query);
  if (normalized.length < MIN_QUERY_LENGTH) {
    return loadDirectoryRecentSearches();
  }

  const prev = loadDirectoryRecentSearches();
  const lower = normalized.toLowerCase();
  const withoutRelated = prev.filter((q) => {
    const ql = q.toLowerCase();
    if (ql === lower) return false;
    // Drop shorter/longer typing prefixes of the same query family.
    if (ql.startsWith(lower) || lower.startsWith(ql)) return false;
    return true;
  });

  return persist([normalized, ...withoutRelated].slice(0, MAX_RECENT));
}

/** Remove one recent query (case-insensitive). */
export function removeDirectoryRecentSearch(query: string): string[] {
  const normalized = normalize(query).toLowerCase();
  const next = loadDirectoryRecentSearches().filter(
    (q) => q.toLowerCase() !== normalized,
  );
  return persist(next);
}

export function clearDirectoryRecentSearches(): string[] {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
  emitChanged([]);
  return [];
}
